import _, {findLast, findLastIndex, first, isUndefined, last} from 'lodash';
import {Key} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  isActivityInConversationRelevant,
  isActivityInConversationRuleNotification,
  isActivityInConversationUnreadMention,
  isConversationInTeamContext,
} from '../../../../../src/helpers/models/conversationHelpers';
import {ActivityModel, ActivityVisualTypesEnum} from '../../../../../src/models/api/activityModel';
import {ConversationModel} from '../../../../../src/models/api/conversationModel';
import {DraftMessageModel, TimelineMessageModel} from '../../../../../src/models/api/messageModel';
import {AsyncMoreStatusesEnum} from '../../../../../src/store/appTypes';
import {
  findParametersActivityDate,
  findParametersActivityId,
} from '../../../../../src/store/panels/inboxes/parameters/inboxesParametersHelpers';
import {SendingActivity} from '../../../../../src/store/panels/viewer/viewerPanelSelectors';
import {ViewerPanelConversationPageState} from '../../../../../src/store/panels/viewer/viewerPanelState';

/*
 * Constants.
 */

export const MILESTONE_ACTIVITY = 'milestone-newactivity';

/*
 * Helpers.
 */

export interface LastTimelineMessage {
  activityId?: number;
  orphanSentMessageUid?: string;
  sendingActivityIndex?: number;
}

/** @returns the last message that is displayed in the timeline. */
export function findLastTimelineMessage(
  activities: ReadonlyArray<ActivityModel>,
  orphanSentMessages: ReadonlyArray<DraftMessageModel | TimelineMessageModel>,
  sendingActivities: ReadonlyArray<SendingActivity>,
  hasComposeDrafts: boolean,
) {
  if (hasComposeDrafts) {
    // A draft is the last message.
    return {};
  }

  const lastSendingMessageActivityIndex = findLastIndex(sendingActivities, (a) => isDefined(a.message));
  if (lastSendingMessageActivityIndex !== -1) {
    // We have a sending message, which is the last in the timeline.
    return {
      sendingActivityIndex: lastSendingMessageActivityIndex,
    };
  }

  const lastOrphanSentMessage = last(orphanSentMessages);
  if (lastOrphanSentMessage) {
    // We have a sent message without an activity, which is the last in the timeline.
    return {
      orphanSentMessageUid: lastOrphanSentMessage.uid,
    };
  }

  const lastMessageActivity = findLast(activities, (a) => a.visualType === ActivityVisualTypesEnum.MESSAGE);
  if (lastMessageActivity) {
    // We have a message activity, which is the last in the timeline.
    return {
      activityId: lastMessageActivity.id,
    };
  }

  // There is no last message.
  return {};
}

/** Returns the initial activity to focus. */
interface FindFocusKeyProps {
  page: ViewerPanelConversationPageState;
  activities: ReadonlyArray<ActivityModel>;
  conversation: ConversationModel;
  userAlias: string;
  composeDraftMessages: ReadonlyArray<DraftMessageModel>;
}

export function findFocusKey(props: FindFocusKeyProps): Key | undefined {
  const {page, activities, conversation, userAlias, composeDraftMessages} = props;
  const {cellsContext, originalConversationState} = page;
  const aroundActivityId = findParametersActivityId(page.parameters);
  const aroundActivityDate = findParametersActivityDate(page.parameters);

  // If this is a demo conversation, just return the first activity.
  if (conversation.isGuidedDemo) {
    return first(activities)?.id;
  }

  // If the top was requested, focus the first activity if we have it.
  if (aroundActivityDate === 0) {
    return page.moreStatusTop === AsyncMoreStatusesEnum.ALL_LOADED ? first(activities)?.id : undefined;
  }

  // If a specific activity was requested, focus it.
  if (!isUndefined(aroundActivityId)) {
    // We may have negative id in case we matched a draft.
    if (aroundActivityId < 0) {
      const draftToFocus = composeDraftMessages.find((draft) => draft.id === Math.abs(aroundActivityId));
      return draftToFocus?.uid;
    }

    return activities.find((a) => a.id === aroundActivityId)?.id;
  }

  // If a specific date was requested, focus the nearest activity.
  if (!isUndefined(aroundActivityDate)) {
    return _(activities)
      .filter(isActivityInConversationRelevant)
      .findLast((a) => a.date <= aroundActivityDate)?.id;
  }

  const lastActivityId = findLast(activities, isActivityInConversationRelevant)?.id;

  // In a team context, start at the newest message or comment by default.
  if (isConversationInTeamContext(cellsContext, conversation)) {
    return lastActivityId;
  }

  if (!originalConversationState) {
    return lastActivityId;
  }

  const {seenAt, autoSeenAt} = originalConversationState;

  // If you've been at-mentioned, scroll to first unread mention, should not scroll if you have marked a conversation as unread.
  const unreadUserMention = activities.find((a) =>
    isActivityInConversationUnreadMention(a, userAlias, autoSeenAt),
  );

  if (unreadUserMention) {
    return unreadUserMention.id;
  }

  // If you've never seen the conversation start at the beginning.
  if (!seenAt) {
    return first(activities)?.id;
  }

  const lastReadActivity = findLast(
    activities,
    (a) => isActivityInConversationRelevant(a) && a.date <= seenAt,
  );

  // If we've read everything and we have a draft, focus the last draft.
  const lastDraftComposeUid = last(composeDraftMessages)?.uid;
  if (lastDraftComposeUid && lastReadActivity?.id === lastActivityId) {
    return lastDraftComposeUid;
  }

  // If there are unread messages, focus the new activity line.
  const firstUnreadActivity = activities.find((a) => isActivityInConversationRelevant(a) && a.date > seenAt);
  if (firstUnreadActivity) {
    return MILESTONE_ACTIVITY;
  }

  // If unread rule notification, focus there to show why you're notified on the conversation
  const unreadRuleNotification = activities.find((a) =>
    isActivityInConversationRuleNotification(a, autoSeenAt),
  );
  if (unreadRuleNotification) {
    return unreadRuleNotification?.id;
  }

  // Finally, if no other focus activity, focus the last relevant activity.
  return lastActivityId;
}

export function maybeUpdateFirstVisibleActivityDate(
  page: ViewerPanelConversationPageState,
  firstVisibleActivityDate: number,
  setFirstVisibleActivityDate: HandlerOf<number>,
  setIsAtTopOfTimeline: HandlerOf<boolean>,
  firstActivity?: ActivityModel,
  firstVisibleActivity?: ActivityModel,
) {
  if (!firstVisibleActivity || !page.originalConversationState) {
    return;
  }

  const originalSeenAt = page.originalConversationState.seenAt;

  // If our first visible activity is earlier than the previous visible, update earliest visible date
  if (
    firstVisibleActivity.date < firstVisibleActivityDate &&
    (!originalSeenAt || firstVisibleActivity.date > originalSeenAt)
  ) {
    setFirstVisibleActivityDate(firstVisibleActivity.date);
  }

  // Otherwise, if we've seen this conversation before and we're viewing already viewed activities,
  // set our originalSeenAt date as the earliest viewed activity
  if (originalSeenAt && firstVisibleActivity.date < originalSeenAt) {
    setFirstVisibleActivityDate(originalSeenAt);
  }

  // If we're looking at the top of the timeline, we have no need to load the unread banner
  if (firstActivity && firstVisibleActivity.date <= firstActivity.date) {
    setIsAtTopOfTimeline(true);
  } else {
    setIsAtTopOfTimeline(false);
  }
}

export function maybeUpdateLastVisibleActivityDate(
  lastVisibleActivityDate: number,
  lastVisibleActivityId: Key,
  isAtBottomOfTimeline: boolean,
  setIsAtBottomOfTimeline: HandlerOf<boolean>,
  setLastVisibleActivityDate: HandlerOf<number>,
  firstVisibleActivity?: ActivityModel,
  lastVisibleActivity?: ActivityModel,
) {
  // If the activity is not in our activities list it's generally a draft/recently sent
  if (!lastVisibleActivity && lastVisibleActivityId !== MILESTONE_ACTIVITY) {
    setIsAtBottomOfTimeline(true);
  }

  // If we're at the bottom of the timeline but we can see activities above it, we can still see
  // new incoming messages
  if (isAtBottomOfTimeline && firstVisibleActivity) {
    setLastVisibleActivityDate(Date.now());
  }

  if (!lastVisibleActivity) {
    return;
  }

  setIsAtBottomOfTimeline(false);
  if (lastVisibleActivity.date > lastVisibleActivityDate) {
    setLastVisibleActivityDate(lastVisibleActivity.date);
  }
}
